import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  MessageCircle, Settings, FileText, History, Send, Plus, Pencil, Trash2, 
  TestTube, Phone, CheckCircle, XCircle, Clock, RefreshCw, Eye
} from 'lucide-react';
import api from '../../utils/api';

const WhatsAppSettings = () => {
  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState({
    api_key: '',
    sender_number: '',
    enabled: false,
    base_url: 'https://api.qikchat.in/v1/messages'
  });
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  
  // Test send state
  const [testMobile, setTestMobile] = useState('');
  const [testVariables, setTestVariables] = useState({});
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testingTemplate, setTestingTemplate] = useState(null);
  
  // Logs state
  const [logs, setLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  
  useEffect(() => {
    fetchSettings();
    fetchTemplates();
  }, []);
  
  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    }
  }, [activeTab, logsPage]);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/whatsapp/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching WhatsApp settings:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/admin/whatsapp/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/whatsapp/logs?page=${logsPage}&limit=20`);
      setLogs(response.data.data || []);
      setLogsTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await api.put('/admin/whatsapp/settings', settings);
      toast.success('WhatsApp settings saved successfully');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!testMobile || testMobile.length < 10) {
      toast.error('Please enter a valid mobile number');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/admin/whatsapp/test-connection', { mobile: testMobile });
      toast.success(response.data.message || 'Test message sent successfully');
    } catch (error) {
      const errMsg = error.response?.data?.detail || error.response?.data?.error || error.message || 'Failed to send test message';
      toast.error(`Error: ${errMsg}`);
      console.error('WhatsApp test-connection error:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    setLoading(true);
    try {
      if (editingTemplate.id && templates.find(t => t.key === editingTemplate.key)) {
        await api.put(`/admin/whatsapp/templates/${editingTemplate.key}`, editingTemplate);
        toast.success('Template updated successfully');
      } else {
        await api.post('/admin/whatsapp/templates', editingTemplate);
        toast.success('Template created successfully');
      }
      setShowTemplateDialog(false);
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateKey) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await api.delete(`/admin/whatsapp/templates/${templateKey}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleTestTemplate = async () => {
    if (!testMobile || testMobile.length < 10) {
      toast.error('Please enter a valid mobile number');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post(`/admin/whatsapp/templates/${testingTemplate.key}/test`, {
        mobile: testMobile,
        variables: testVariables
      });
      toast.success(response.data.message || 'Test message sent!');
      setShowTestDialog(false);
      setTestingTemplate(null);
      setTestMobile('');
      setTestVariables({});
    } catch (error) {
      const errMsg = error.response?.data?.detail || error.response?.data?.error || error.message || 'Failed to send test message';
      toast.error(`Error: ${errMsg}`);
      console.error('WhatsApp template test error:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const openTestDialog = (template) => {
    setTestingTemplate(template);
    // Initialize variables based on template
    const vars = {};
    (template.variables || []).forEach((v, i) => {
      vars[String(i + 1)] = '';
    });
    setTestVariables(vars);
    setShowTestDialog(true);
  };

  const openEditDialog = (template = null) => {
    if (template) {
      setEditingTemplate({ ...template });
    } else {
      setEditingTemplate({
        key: '',
        name: '',
        description: '',
        type: 'transactional',
        trigger: '',
        template_id: 'sample',
        message_preview: '',
        variables: [],
        enabled: true,
        recipient_type: 'job_seeker'
      });
    }
    setShowTemplateDialog(true);
  };

  const initializeTemplates = async () => {
    setLoading(true);
    try {
      const response = await api.post('/admin/whatsapp/templates/initialize');
      toast.success(response.data.message);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to initialize templates');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'templates', label: 'Templates', icon: FileText },
    { id: 'logs', label: 'Message Logs', icon: History }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MessageCircle className="h-6 w-6 text-green-600" />
            WhatsApp Settings
          </h1>
          <p className="text-slate-600">Configure WhatsApp messaging via Qikberry API</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <Card>
          <CardHeader>
            <CardTitle>API Configuration</CardTitle>
            <CardDescription>Configure Qikberry WhatsApp API settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Enable WhatsApp Messaging</Label>
                <p className="text-sm text-slate-500">Turn on/off all WhatsApp notifications</p>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="api_key">API Key *</Label>
                <Input
                  id="api_key"
                  type="password"
                  value={settings.api_key}
                  onChange={(e) => setSettings({ ...settings, api_key: e.target.value })}
                  placeholder="Enter Qikberry API Key"
                />
                {settings.api_key_masked && (
                  <p className="text-xs text-slate-500">Current: {settings.api_key_masked}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="sender_number">Sender Number</Label>
                <Input
                  id="sender_number"
                  type="text"
                  value={settings.sender_number}
                  onChange={(e) => setSettings({ ...settings, sender_number: e.target.value })}
                  placeholder="919876543210"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="base_url">API Base URL</Label>
                <Input
                  id="base_url"
                  type="text"
                  value={settings.base_url}
                  onChange={(e) => setSettings({ ...settings, base_url: e.target.value })}
                  placeholder="https://api.qikchat.in/v1/messages"
                />
              </div>
            </div>

            <div className="flex items-center gap-4 pt-4 border-t">
              <Button onClick={handleSaveSettings} disabled={loading}>
                {loading ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>

            {/* Test Connection */}
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h3 className="font-medium text-green-800 mb-3">Test Connection</h3>
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <Label htmlFor="test_mobile">Mobile Number</Label>
                  <Input
                    id="test_mobile"
                    type="text"
                    value={testMobile}
                    onChange={(e) => setTestMobile(e.target.value)}
                    placeholder="9876543210"
                    maxLength={10}
                  />
                </div>
                <Button 
                  onClick={handleTestConnection} 
                  disabled={loading || !testMobile}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Test
                </Button>
              </div>
              <p className="text-xs text-green-700 mt-2">
                This will send a test message using the 'sample' template to verify API connectivity.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-slate-600">Manage WhatsApp message templates</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={initializeTemplates} disabled={loading}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Initialize Defaults
              </Button>
              <Button onClick={() => openEditDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Add Template
              </Button>
            </div>
          </div>

          <div className="grid gap-4">
            {templates.map((template) => (
              <Card key={template.key} className={!template.enabled ? 'opacity-60' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-900">{template.name}</h3>
                        <Badge variant={template.enabled ? 'default' : 'secondary'}>
                          {template.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {template.type}
                        </Badge>
                        <Badge variant="outline" className="text-xs capitalize">
                          {template.recipient_type?.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 mb-2">{template.description}</p>
                      <div className="text-xs text-slate-500 space-y-1">
                        <p><strong>Key:</strong> {template.key}</p>
                        <p><strong>Template ID:</strong> {template.template_id}</p>
                        <p><strong>Trigger:</strong> {template.trigger}</p>
                        {template.message_preview && (
                          <p className="bg-slate-100 p-2 rounded mt-2 font-mono text-xs">
                            {template.message_preview}
                          </p>
                        )}
                        {template.variables?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {template.variables.map((v, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {v.placeholder}: {v.description}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openTestDialog(template)}
                        className="text-green-600 border-green-300 hover:bg-green-50"
                      >
                        <TestTube className="h-4 w-4 mr-1" />
                        Test
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(template)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteTemplate(template.key)}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {templates.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <MessageCircle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No templates found. Click "Initialize Defaults" to create default templates.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Message Logs</CardTitle>
                <CardDescription>History of sent WhatsApp messages</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={fetchLogs} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="delete-all-logs-btn"
                  onClick={async () => {
                    if (!window.confirm('Delete ALL WhatsApp message logs? This cannot be undone.')) return;
                    try {
                      const r = await api.delete('/admin/whatsapp/logs/all');
                      toast.success(r.data?.message || 'All logs deleted');
                      setLogsPage(1);
                      fetchLogs();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to delete logs');
                    }
                  }}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Delete All
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {logs.map((log, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {log.status === 'sent' && <CheckCircle className="h-5 w-5 text-green-500" />}
                    {log.status === 'failed' && <XCircle className="h-5 w-5 text-red-500" />}
                    {log.status === 'pending' && <Clock className="h-5 w-5 text-yellow-500" />}
                    {log.status === 'error' && <XCircle className="h-5 w-5 text-red-500" />}
                    <div>
                      <p className="font-medium text-slate-900">
                        <Phone className="h-3 w-3 inline mr-1" />
                        {log.to_number}
                      </p>
                      <p className="text-sm text-slate-600">
                        Template: {log.template_key}
                        {log.test_mode && <Badge variant="outline" className="ml-2 text-xs">Test</Badge>}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={log.status === 'sent' ? 'default' : log.status === 'failed' ? 'destructive' : 'secondary'}>
                      {log.status}
                    </Badge>
                    <p className="text-xs text-slate-500 mt-1">
                      {new Date(log.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}

              {logs.length === 0 && (
                <p className="text-center text-slate-500 py-8">No message logs found</p>
              )}

              {logsTotalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={logsPage === 1}
                    onClick={() => setLogsPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-slate-600">
                    Page {logsPage} of {logsTotalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={logsPage === logsTotalPages}
                    onClick={() => setLogsPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate?.id ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
          </DialogHeader>
          
          {editingTemplate && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Template Key *</Label>
                  <Input
                    value={editingTemplate.key}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, key: e.target.value })}
                    placeholder="e.g., job_seeker_welcome"
                    disabled={!!editingTemplate.id}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Template Name *</Label>
                  <Input
                    value={editingTemplate.name}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                    placeholder="e.g., Job Seeker Welcome"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={editingTemplate.description}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
                  placeholder="Brief description of when this template is used"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Qikberry Template ID *</Label>
                  <Input
                    value={editingTemplate.template_id}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, template_id: e.target.value })}
                    placeholder="sample"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <select
                    className="w-full h-10 px-3 border rounded-md"
                    value={editingTemplate.type}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, type: e.target.value })}
                  >
                    <option value="transactional">Transactional</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="promotional">Promotional</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Recipient Type</Label>
                  <select
                    className="w-full h-10 px-3 border rounded-md"
                    value={editingTemplate.recipient_type}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, recipient_type: e.target.value })}
                  >
                    <option value="job_seeker">Job Seeker</option>
                    <option value="employer">Employer</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Trigger Event</Label>
                <Input
                  value={editingTemplate.trigger}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, trigger: e.target.value })}
                  placeholder="e.g., job_seeker_registration, employer_approval"
                />
              </div>

              <div className="space-y-2">
                <Label>Message Preview</Label>
                <Textarea
                  value={editingTemplate.message_preview}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, message_preview: e.target.value })}
                  placeholder="Hello {{1}}, welcome to VVVSI! {{2}}"
                  rows={3}
                />
                <p className="text-xs text-slate-500">Use {'{{1}}'}, {'{{2}}'}, etc. for dynamic variables</p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Variables</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingTemplate({
                      ...editingTemplate,
                      variables: [...(editingTemplate.variables || []), { placeholder: `{{${(editingTemplate.variables?.length || 0) + 1}}}`, field: '', description: '' }]
                    })}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Variable
                  </Button>
                </div>
                {editingTemplate.variables?.map((v, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                    <Input
                      value={v.placeholder}
                      className="w-20"
                      readOnly
                    />
                    <Input
                      value={v.field}
                      onChange={(e) => {
                        const vars = [...editingTemplate.variables];
                        vars[i].field = e.target.value;
                        setEditingTemplate({ ...editingTemplate, variables: vars });
                      }}
                      placeholder="Field name"
                      className="flex-1"
                    />
                    <Input
                      value={v.description}
                      onChange={(e) => {
                        const vars = [...editingTemplate.variables];
                        vars[i].description = e.target.value;
                        setEditingTemplate({ ...editingTemplate, variables: vars });
                      }}
                      placeholder="Description"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const vars = editingTemplate.variables.filter((_, idx) => idx !== i);
                        setEditingTemplate({ ...editingTemplate, variables: vars });
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={editingTemplate.enabled}
                  onCheckedChange={(checked) => setEditingTemplate({ ...editingTemplate, enabled: checked })}
                />
                <Label>Enabled</Label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate} disabled={loading}>
              {loading ? 'Saving...' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Template Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test Template: {testingTemplate?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Mobile Number *</Label>
              <Input
                value={testMobile}
                onChange={(e) => setTestMobile(e.target.value)}
                placeholder="9876543210"
                maxLength={10}
              />
            </div>

            {testingTemplate?.variables?.map((v, i) => (
              <div key={i} className="space-y-2">
                <Label>{v.placeholder} - {v.description}</Label>
                <Input
                  value={testVariables[String(i + 1)] || ''}
                  onChange={(e) => setTestVariables({ ...testVariables, [String(i + 1)]: e.target.value })}
                  placeholder={`Enter ${v.description}`}
                />
              </div>
            ))}

            {testingTemplate?.message_preview && (
              <div className="p-3 bg-slate-100 rounded-lg">
                <Label className="text-xs">Message Preview:</Label>
                <p className="font-mono text-sm mt-1">{testingTemplate.message_preview}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleTestTemplate} disabled={loading} className="bg-green-600 hover:bg-green-700">
              <Send className="h-4 w-4 mr-2" />
              {loading ? 'Sending...' : 'Send Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppSettings;
